import { useCallback, useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import type { FunctionReference } from "convex/server";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { PageHeader } from "@/components/layout";
import {
  ProjectDocumentPicker,
  type PickerDocument,
  type PickerSection,
  type SectionSelections,
} from "@/components/project-document-picker";
import { useProject } from "@/components/projects";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAppNavigate } from "@/lib/use-app-navigate";

export const Route = createFileRoute("/procurePilot/new")({
  component: NewProcurePilotPage,
});

type ProcurePilotJobsApi = {
  createFromRevision: FunctionReference<
    "mutation",
    "public",
    { projectId: string; revisionId: string; name: string },
    string
  >;
};

const procurePilotApi = (api as unknown as { procurePilot: { jobs: ProcurePilotJobsApi } })
  .procurePilot.jobs;

const formSchema = z.object({
  name: z.string().min(1, "ProcurePilot job name is required"),
});

type FormValues = z.infer<typeof formSchema>;

export function NewProcurePilotPage() {
  const navigate = useAppNavigate();
  const { projectId, project, isLoading: isProjectLoading } = useProject();
  const createJob = useMutation(procurePilotApi.createFromRevision);
  const [pickerValues, setPickerValues] = useState<Record<string, SectionSelections>>({
    schedules: { mode: "single", selection: null },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUserEditedName, setHasUserEditedName] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema as never),
    defaultValues: { name: "" },
  });

  const { data: scheduleOptionsRaw } = useQuery(
    convexQuery(
      api.projectDocumentRevisionOptions.listClassifiedPdfRevisionOptions,
      projectId ? { projectId, documentType: "schedule" } : "skip"
    )
  );

  const selectedRevisionId =
    pickerValues.schedules?.mode === "single"
      ? pickerValues.schedules.selection?.revisionId
      : undefined;
  const selectedDocumentId =
    pickerValues.schedules?.mode === "single"
      ? pickerValues.schedules.selection?.documentId
      : undefined;

  const scheduleDocs: PickerDocument[] = (scheduleOptionsRaw ?? []).map((opt) => ({
    _id: String(opt._id),
    name: opt.name,
    revisions: opt.revisions.map((revision) => ({
      _id: String(revision._id),
      revisionNumber: revision.revisionNumber,
      revisionName: revision.revisionName,
    })),
  }));
  const sections: PickerSection[] = [
    {
      key: "schedules",
      label: "Schedules",
      documents: scheduleDocs,
      selectionMode: "single",
      revisionMode: "single",
    },
  ];

  useEffect(() => {
    if (!isProjectLoading && !projectId) {
      navigate({ to: "/projects", title: "Projects" });
    }
  }, [isProjectLoading, projectId, navigate]);

  useEffect(() => {
    if (!selectedRevisionId || !selectedDocumentId) return;
    const doc = scheduleOptionsRaw?.find((option) => String(option._id) === selectedDocumentId);
    const revision = doc?.revisions.find((item) => String(item._id) === selectedRevisionId);
    if (revision && !hasUserEditedName) {
      form.setValue("name", `${revision.originalFilename.replace(/\.pdf$/i, "")} Procurement`, {
        shouldValidate: true,
      });
    }
  }, [selectedRevisionId, selectedDocumentId, scheduleOptionsRaw, form, hasUserEditedName]);

  const handlePickerChange = useCallback((sectionKey: string, value: SectionSelections) => {
    setPickerValues((prev) => ({ ...prev, [sectionKey]: value }));
  }, []);

  const onSubmit = async (values: FormValues) => {
    if (!projectId || !selectedRevisionId) {
      toast.error("Select a project schedule first");
      return;
    }
    setIsSubmitting(true);
    try {
      const jobId = await createJob({
        projectId,
        revisionId: selectedRevisionId as Id<"projectDocumentRevisions">,
        name: values.name,
      });
      toast.success("ProcurePilot job created");
      void navigate({ to: "/procurePilot/$jobId", params: { jobId } });
    } catch (error) {
      console.error(error);
      toast.error("Failed to create ProcurePilot job");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="New ProcurePilot Job"
        description="Select a schedule, then choose the tables and rows to procure."
        breadcrumbs={[{ label: "ProcurePilot", href: "/procurePilot" }, { label: "New ProcurePilot Job" }]}
      />
      <Card className="max-w-2xl border-2 shadow-sm">
        <CardHeader>
          <CardTitle>Create ProcurePilot Job</CardTitle>
          <CardDescription>
            {project ? `Creating in project: ${project.name}.` : "Select a project schedule."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ProcurePilot Job Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter ProcurePilot job name"
                        disabled={isSubmitting}
                        {...field}
                        onChange={(event) => {
                          setHasUserEditedName(true);
                          field.onChange(event);
                        }}
                      />
                    </FormControl>
                    <FormDescription>Auto-populated when you select a schedule revision.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <ProjectDocumentPicker
                sections={sections}
                values={pickerValues}
                onChange={handlePickerChange}
                isLoading={scheduleOptionsRaw === undefined}
                disabled={isSubmitting}
              />
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" disabled={isSubmitting} onClick={() => navigate({ to: "/procurePilot" })}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting || !selectedRevisionId}>
                  {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : null}
                  Create Job
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
